import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { daDate } from "@/lib/date";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ST_BADGE, ActionFormDialog } from "@/components/le/ActionUI";
import { FollowupCardCompact } from "@/components/le/FollowupCard";
import { GOAL_ST_BADGE, GOAL_STATUSES, GoalFormDialog } from "@/pages/LeGoals";
import { Card, SectionTitle, FileRow, DetailGrid } from "@/components/le/DetailLayout";
import { Timeline } from "@/components/le/ActionSections";
import { OutcomePicker, OutcomeBadge, OUTCOME_LABEL } from "@/components/le/OutcomePicker";
import { ChevronLeft, Loader2, Target, Plus, Pencil, Paperclip, Upload, Camera, Compass, ListChecks, History, Flag } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function goalTimeline(g) {
  const ev = [];
  if (g.created_at) ev.push({ kind: "created", at: g.created_at, by: g.created_by_name, text: "Mål oprettet" });
  (g.change_log || []).forEach((c, i) => ev.push({ kind: c.field === "deadline" ? "deadline" : "responsible", at: c.at, by: c.by_name, key: `cl-${i}`,
    text: c.field === "deadline" ? `Frist ændret: ${c.from ? daDate(c.from) : "ingen"} → ${c.to ? daDate(c.to) : "ingen"}` : `Ansvarlig ændret: ${c.from || "—"} → ${c.to || "—"}` }));
  (g.evaluations || []).forEach((e) => ev.push({ kind: e.status_to === "Lukket" ? "closed" : e.status_from === "Lukket" ? "reopened" : "note", at: e.at, by: e.by_name, key: e.id, meeting: e.meeting_title,
    text: e.status_to ? `Status: ${e.status_from || "—"} → ${e.status_to}${e.outcome ? ` · Udfald: ${OUTCOME_LABEL[e.outcome] || e.outcome}` : ""}` : null, comment: e.text }));
  return ev.sort((a, b) => (b.at || "").localeCompare(a.at || ""));
}

export default function LeGoalDetail() {
  const { gid } = useParams();
  const nav = useNavigate();
  const [g, setG] = useState(null);
  const [links, setLinks] = useState(null);
  const [users, setUsers] = useState([]);
  const [text, setText] = useState("");
  const [evalStatus, setEvalStatus] = useState("");
  const [outcome, setOutcome] = useState(null);
  const [busy, setBusy] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [actionOpen, setActionOpen] = useState(false);
  const fileRef = useRef(null);
  const camRef = useRef(null);
  const merge = (d) => setG((p) => ({ ...p, ...d }));

  const load = async () => {
    try {
      const r = await api.get(`/le/goals/${gid}`);
      setG(r.data); setEvalStatus(r.data.status);
      if (r.data.can_admin) api.get("/users").then((u) => setUsers(u.data)).catch(() => {});
      api.get("/le/action-links", { params: { type: "goal", id: gid } }).then((l) => setLinks(l.data)).catch(() => {});
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); nav("/"); }
  };
  useEffect(() => { load(); }, [gid]); // eslint-disable-line

  const addEval = async () => {
    if (!text.trim() && evalStatus === g.status) return toast.error("Skriv en evaluering eller skift status");
    setBusy(true);
    try {
      const r = await api.post(`/le/goals/${gid}/evaluation`, { text, new_status: evalStatus !== g.status ? evalStatus : undefined, outcome: evalStatus === "Lukket" ? outcome : undefined });
      merge(r.data); setText(""); setEvalStatus(r.data.status); toast.success("Evaluering tilføjet");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy(false);
  };
  const uploadFile = async (fileList) => {
    const files = Array.from(fileList || []); if (!files.length) return;
    setBusy(true);
    try { for (const file of files) { const fd = new FormData(); fd.append("file", file); const r = await api.post(`/le/goals/${gid}/files`, fd); merge(r.data); } toast.success("Fil tilføjet"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy(false);
  };
  const delFile = async (id) => {
    if (!window.confirm("Fjern denne fil?")) return;
    setBusy(true);
    try { const r = await api.delete(`/le/goals/${gid}/files/${id}`); merge(r.data); toast.success("Fil fjernet"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy(false);
  };

  if (!g) return <div className="flex items-center gap-2 text-stone-400"><Loader2 className="h-4 w-4 animate-spin" /> Indlæser…</div>;
  const deadlineMoves = (g.change_log || []).filter((c) => c.field === "deadline").length;
  const linked = (links?.actions || g.linked_actions || []);

  const hiddenInputs = (
    <>
      <input ref={fileRef} type="file" multiple className="hidden" data-testid="le-goal-file-upload" onChange={(e) => { uploadFile(e.target.files); e.target.value = ""; }} />
      <input ref={camRef} type="file" accept="image/*" capture="environment" className="hidden" data-testid="le-goal-camera-upload" onChange={(e) => { uploadFile(e.target.files); e.target.value = ""; }} />
    </>
  );
  const uploadButtons = (sfx = "") => (
    <>
      <button type="button" data-testid={`le-goal-add-file${sfx}`} onClick={() => fileRef.current?.click()} disabled={busy} className="h-9 px-3 rounded-xl border border-[#EBE8E3] text-sm text-[#806B61] flex items-center gap-1.5 hover:bg-[#FBFAF8]"><Upload className="h-4 w-4" /> Tilføj fil</button>
      <button type="button" data-testid={`le-goal-add-photo${sfx}`} onClick={() => camRef.current?.click()} disabled={busy} className="h-9 px-3 rounded-xl border border-[#EBE8E3] text-sm text-[#806B61] flex items-center gap-1.5 hover:bg-[#FBFAF8] lg:hidden"><Camera className="h-4 w-4" /> Tag foto</button>
    </>
  );

  const panel = (
    <>
      <div>
        <label className="text-xs font-medium text-stone-600 mb-1.5 block">Status</label>
        <div className="inline-flex rounded-xl border border-[#EBE8E3] p-1 flex-wrap">
          {GOAL_STATUSES.map((s) => <button key={s} type="button" data-testid={`le-goal-eval-status-${s}`} disabled={busy} onClick={() => setEvalStatus(s)} className={`px-3 py-1.5 rounded-lg text-sm ${evalStatus === s ? "bg-[#A83232] text-white" : "text-stone-600"}`}>{s}</button>)}
        </div>
      </div>
      {evalStatus === "Lukket" && g.status !== "Lukket" && <OutcomePicker value={outcome} onChange={setOutcome} testid="le-goal-outcome" />}
      <div>
        <label className="text-xs font-medium text-stone-600 mb-1 block">Ny evaluering</label>
        <Textarea data-testid="le-goal-eval-text" value={text} onChange={(e) => setText(e.target.value)} className="rounded-xl min-h-[70px]" placeholder="Hvordan går det med målet? Fremdrift, målinger, næste skridt…" />
        <div className="flex justify-end mt-2"><Button data-testid="le-goal-eval-save" onClick={addEval} disabled={busy} className="rounded-xl bg-[#A83232] hover:bg-[#8f2a2a]">Tilføj evaluering</Button></div>
      </div>
      <div className="flex flex-wrap gap-2 border-t border-[#F0EDE8] pt-4">
        {uploadButtons()}
        {g.can_admin && <button type="button" data-testid="le-goal-add-action" onClick={() => setActionOpen(true)} className="h-9 px-3 rounded-xl border border-[#EBE8E3] text-sm text-[#806B61] flex items-center gap-1.5 hover:bg-[#FBFAF8]"><Plus className="h-4 w-4" /> Opret handling</button>}
        {g.can_admin && <button type="button" data-testid="le-goal-edit" onClick={() => setEditOpen(true)} className="h-9 px-3 rounded-xl border border-[#EBE8E3] text-sm text-[#806B61] flex items-center gap-1.5 hover:bg-[#FBFAF8]"><Pencil className="h-4 w-4" /> Redigér mål</button>}
      </div>
    </>
  );

  return (
    <div className="space-y-5 max-w-6xl" data-testid="le-goal-detail-page">
      {hiddenInputs}
      <button onClick={() => nav(-1)} className="text-sm text-stone-500 hover:text-stone-900 flex items-center gap-1" data-testid="le-goal-back"><ChevronLeft className="h-4 w-4" /> Tilbage</button>

      <Card className="p-5" data-testid="le-goal-header">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs font-bold uppercase tracking-[0.15em] text-[#A83232] flex items-center gap-1.5"><Target className="h-3.5 w-3.5" /> Kvalitets- & miljømål</span>
          {g.goal_no && <span className="text-xs font-mono text-stone-400">{g.goal_no}</span>}
          {g.category && <span className="text-[11px] px-2 py-0.5 rounded-full bg-[#F4EFEC] text-[#806B61]">{g.category}</span>}
          <span className={`text-[11px] px-2 py-0.5 rounded-full ${GOAL_ST_BADGE[g.status] || ""}`} data-testid="le-goal-status-badge">{g.status}</span>
          <OutcomeBadge goal={g} testid="le-goal-outcome-badge" />
          {g.overdue && <span className="text-[11px] px-2 py-0.5 rounded-full bg-white text-[#A83232] border border-[#E9C9C3] flex items-center gap-1"><Flag className="h-3 w-3" /> Overskredet</span>}
          {deadlineMoves > 0 && <span className="text-[11px] px-2 py-0.5 rounded-full bg-[#EFE7DC] text-[#8A6D3B] border border-[#E3D5C0]" data-testid="le-goal-deadline-moved">Frist rykket {deadlineMoves}×</span>}
        </div>
        <h1 className="text-2xl font-semibold text-[#1A1919] mt-2 whitespace-pre-wrap">{g.title}</h1>
        <div className="flex flex-wrap gap-x-6 gap-y-1.5 mt-3 text-sm">
          <span className="text-stone-500">Ansvarlig <span className="text-[#1A1919] font-medium">{g.responsible_name || "—"}</span>{(g.co_responsible_names || []).length > 0 && <span className="text-stone-400"> · med {g.co_responsible_names.join(", ")}</span>}</span>
          <span className="text-stone-500">Frist <span className={`font-medium ${g.overdue ? "text-[#A83232]" : "text-[#1A1919]"}`}>{g.deadline ? daDate(g.deadline) : "Ingen frist"}</span>
            {g.original_deadline && g.original_deadline !== g.deadline && <span className="text-stone-400 line-through ml-1.5">{daDate(g.original_deadline)}</span>}</span>
          <span className="text-stone-500">Start <span className="text-[#1A1919] font-medium">{g.start_date ? daDate(g.start_date) : "—"}</span></span>
          {g.created_by_name && <span className="text-stone-500">Oprettet af <span className="text-[#1A1919] font-medium">{g.created_by_name}</span>{g.created_at ? ` · ${daDate(g.created_at)}` : ""}</span>}
        </div>
      </Card>

      <DetailGrid panel={panel} panelTestid="le-goal-panel">
        <Card className="p-5" data-testid="le-goal-origin-box">
          <SectionTitle icon={Compass} n={1} title="Oprindelse" />
          <div className="rounded-2xl border border-[#E9C9C3] bg-[#FDF8F6] p-4 space-y-3">
            <div><p className="label-caps text-[#A83232]">Udgangspunkt (baseline)</p><p className="text-[15px] text-[#1A1919] mt-1.5 whitespace-pre-wrap">{g.baseline || "—"}</p></div>
            {g.action_plan && <div><p className="label-caps text-[#A83232]">Handlingsplan</p><p className="text-sm text-stone-700 mt-1.5 whitespace-pre-wrap">{g.action_plan}</p></div>}
          </div>
        </Card>

        <Card className="p-5" data-testid="le-goal-actions-section">
          <SectionTitle icon={ListChecks} n={2} title="Fremdrift · tilknyttede handlinger" right={links?.summary && <span className="text-xs text-stone-500" data-testid="le-goal-actions-summary">{links.summary}</span>} />
          {linked.length === 0 ? <p className="text-sm text-stone-400">Ingen handlinger knyttet til målet endnu.</p> : (
            <div className="space-y-2" data-testid="le-goal-actions-list">{linked.map((a) => (
              <div key={a.id} className={a.archived ? "opacity-60" : ""}>
                <FollowupCardCompact action={a} />
                {a.closing_comment && <p className="text-xs text-stone-600 px-3 -mt-1 pb-1"><span className="text-[#5f6b4a] font-medium">Løsning: </span>{a.closing_comment}</p>}
              </div>
            ))}</div>
          )}
        </Card>

        <Card className="p-5" data-testid="le-goal-files-section">
          <SectionTitle icon={Paperclip} n={3} title="Filer & bilag" right={<div className="hidden lg:flex gap-2">{uploadButtons("-section")}</div>} />
          {(g.files || []).length === 0 ? <p className="text-sm text-stone-400">Ingen filer tilføjet endnu.</p> : (
            <div className="space-y-1.5">{g.files.map((fl) => <FileRow key={fl.id} fl={fl} href={`${BACKEND}/api/le/goals/${gid}/files/${fl.id}`} onDelete={() => delFile(fl.id)} disabled={busy} testid={`le-goal-file-${fl.id}`} />)}</div>
          )}
        </Card>

        <Card className="p-5" data-testid="le-goal-timeline-section">
          <SectionTitle icon={History} n={4} title="Tidslinje · evalueringer" />
          <Timeline events={goalTimeline(g)} />
        </Card>
      </DetailGrid>

      {editOpen && <GoalFormDialog open={editOpen} onOpenChange={setEditOpen} users={users} goal={g} onSaved={(d) => merge(d)} />}
      {actionOpen && <ActionFormDialog open={actionOpen} onOpenChange={setActionOpen} users={users} goal={g} onSaved={() => load()} />}
    </div>
  );
}
